import { useFormContext } from 'react-hook-form'
import { useMemo } from 'react'
import Select from 'components/FormUtils/Select'
import { ActionRenderer } from './ActionRenderer'
import apiService from 'services/api'

const triggerOptions = [
  { value: 'deal_created', label: 'Deal created' },
  { value: 'field_changed_account_owner', label: 'Account owner changed' },
  { value: 'field_changed_sales_stage', label: 'Sales stage changed' },
]

// Component for "Account Owner Changed" trigger - includes condition + TO + user
function AccountOwnerChangedTrigger() {
  const { control } = useFormContext()
  const api = apiService()
  const { data: users } = api.useGetUsers()

  const userOptions = useMemo(() => {
    if (!users) return []
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    }))
  }, [users])

  return (
    <div className="flex gap-1 w-full items-center">
      <Select
        name="trigger_type"
        control={control}
        placeholder="Choose condition..."
        options={triggerOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="trigger_target_user_id"
        control={control}
        placeholder="Choose user..."
        options={userOptions}
        className="flex-1"
      />
    </div>
  )
}

// Component for "Sales Stage Changed" trigger - includes condition + TO + stage
function SalesStageChangedTrigger() {
  const { control } = useFormContext()
  const api = apiService()
  const { data: salesStageOptions } = api.useGetSalesStageOptions()

  return (
    <div className="flex gap-1 w-full items-center">
      <Select
        name="trigger_type"
        control={control}
        placeholder="Choose condition..."
        options={triggerOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="trigger_target_stage_id"
        control={control}
        placeholder="Choose sales stage..."
        options={salesStageOptions}
        className="flex-1"
      />
    </div>
  )
}

// Default trigger dropdown when no specific trigger requires additional fields
function DefaultTriggerSelect() {
  const { control } = useFormContext()

  return (
    <Select
      name="trigger_type"
      control={control}
      placeholder="Choose condition..."
      options={triggerOptions}
      className="w-full"
    />
  )
}

// Trigger renderer that switches based on trigger type
function TriggerRenderer({
  selectedTrigger,
}: {
  selectedTrigger: string | null
}) {
  switch (selectedTrigger) {
    case 'field_changed_account_owner':
      return <AccountOwnerChangedTrigger />

    case 'field_changed_sales_stage':
      return <SalesStageChangedTrigger />

    default:
      return <DefaultTriggerSelect />
  }
}

interface TriggerActionStepProps {
  automationId?: number
}

export function TriggerActionStep({ automationId }: TriggerActionStepProps) {
  const { watch } = useFormContext()

  const selectedAction = watch('action_type')
  const selectedTrigger = watch('trigger_type')

  return (
    <div className="space-y-6">
      <div className="bg-[#F5F7F5] rounded-xl p-6 space-y-6 [&_.form-control-select]:!bg-white">
        {/* IF Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">IF</span>
          </div>

          <TriggerRenderer selectedTrigger={selectedTrigger} />
        </div>

        {/* Divider */}
        <div className="border-t border-[#EAECF0]"></div>

        {/* THEN Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">THEN</span>
          </div>

          <div className="flex gap-1 w-full items-center">
            <ActionRenderer
              selectedAction={selectedAction}
              selectedTrigger={selectedTrigger}
              automationId={automationId}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
